import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

const ReadAloudContext = createContext(null);

const LANG_MAP = {
  en: "en-GB", fr: "fr-FR", pt: "pt-PT", sw: "sw-KE", zu: "zu-ZA",
  af: "af-ZA", xh: "xh-ZA", yo: "yo-NG", ig: "ig-NG", ha: "ha-NG",
  es: "es-ES", de: "de-DE", it: "it-IT",
};

function collectPageText() {
  const main = document.querySelector("main");
  if (!main) return "";
  return main.innerText.replace(/\s+/g, " ").trim();
}

// split into speakable chunks (~220 chars, break on sentence boundaries)
function chunkText(text) {
  const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];
  const chunks = [];
  let cur = "";
  for (const s of sentences) {
    if ((cur + s).length > 220) {
      if (cur) chunks.push(cur.trim());
      cur = s;
    } else {
      cur += s;
    }
  }
  if (cur.trim()) chunks.push(cur.trim());
  return chunks.filter(Boolean);
}

export function ReadAloudProvider({ children }) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const chunksRef = useRef([]);
  const idxRef = useRef(0);
  const location = useLocation();

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    chunksRef.current = [];
    idxRef.current = 0;
    setSpeaking(false);
    setPaused(false);
  }, [supported]);

  const speakNext = useCallback(() => {
    if (idxRef.current >= chunksRef.current.length) {
      setSpeaking(false);
      setPaused(false);
      return;
    }
    const lang = LANG_MAP[localStorage.getItem("pula_lang") || "en"] || "en-GB";
    const u = new SpeechSynthesisUtterance(chunksRef.current[idxRef.current]);
    u.lang = lang;
    u.rate = 0.98;
    u.pitch = 1;
    u.onend = () => {
      idxRef.current += 1;
      speakNext();
    };
    u.onerror = () => {
      idxRef.current += 1;
      speakNext();
    };
    window.speechSynthesis.speak(u);
  }, []);

  const start = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const text = collectPageText();
    if (!text) return;
    chunksRef.current = chunkText(text);
    idxRef.current = 0;
    setSpeaking(true);
    setPaused(false);
    speakNext();
  }, [supported, speakNext]);

  const pause = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setPaused(true);
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.resume();
    setPaused(false);
  }, [supported]);

  const toggle = useCallback(() => {
    if (speaking) stop();
    else start();
  }, [speaking, start, stop]);

  // stop reading whenever the route changes
  useEffect(() => { stop(); /* eslint-disable-next-line */ }, [location.pathname]);
  // stop on unmount
  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);

  return (
    <ReadAloudContext.Provider value={{ supported, speaking, paused, start, stop, pause, resume, toggle }}>
      {children}
    </ReadAloudContext.Provider>
  );
}

export const useReadAloud = () => useContext(ReadAloudContext);
